// Mantine
import { DatePickerInput } from '@mantine/dates'
import { Button, TextInput } from '@mantine/core'

// React
import { useState } from 'react'
import { useLocation } from 'react-router-dom'

// Bus
import { useBus, useListener } from 'react-bus'

// Icons
import { IconQrcode, IconCircleCheck } from '@tabler/icons-react'

// Notifications
import { notifications } from '@mantine/notifications'

// Product select
import WarrantyProductSelect from '../products/WarrantyProductSelect'

// Dayjs
import dayjs from 'dayjs'
import 'dayjs/locale/fr'

// Nextjs

// API
import { useAxiosApi } from '@/hooks/useAxiosApi'

export default function WarrantyAssociation() {
    const location = useLocation()

    const [warrantyCodes, setWarrantyCodes] = useState(() => {
        const code = location.state?.warrantyCode
        return typeof code === 'string' && code.trim() ? [code.trim()] : []
    })
    const [codeToAddValue, setCodeToAddValue] = useState('WA')
    const [watchHeadId, setWatchHeadId] = useState(null)
    const [warrantyStartDate, setWarrantyStartDate] = useState(dayjs().toDate())

    const [isDone, setIsDone] = useState(false)

    // Manual add
    const canAddCode = codeToAddValue.length > 5

    const codeToAddValueHandler = (e) => {
        // Check that code is only alphanumeric
        const cleanedValue = e.target.value
            .replace(/[^a-zA-Z0-9]/g, '')
            .toUpperCase()

        if (cleanedValue.length < 11 && cleanedValue.startsWith('WA')) {
            setCodeToAddValue(cleanedValue)
        }
    }

    const addCode = (e) => {
        e.preventDefault()

        if (canAddCode) {
            scanHandler(codeToAddValue)
            setCodeToAddValue('WA')
        }
    }

    // Scan handler
    const scanHandler = (code) => {
        if (!warrantyCodes.includes(code)) {
            setWarrantyCodes((prev) => [...prev, code])

            // Show notification
            notifications.show({
                title: 'Carte ajoutée à la liste',
                message: code,
            })
        }
    }

    // Camera open
    const bus = useBus()

    const openCamera = () => {
        bus.emit('openCameraScanner')
    }

    // Detect camera scan
    useListener('scan', (data) => {
        // Only handle if is a warranty scan
        if (data?.type === 'warranty') {
            scanHandler(data.code)
        }
    })

    // Remove warranty code from list
    const removeWarrantyCode = (code) => {
        setWarrantyCodes((prev) => prev.filter((c) => c !== code))
    }

    // Handle warranty association
    const { requestPost } = useAxiosApi()
    const uniqueWarrantyCodes = [...new Set(warrantyCodes)]
    const canAssociateWarranty = uniqueWarrantyCodes.length > 0 && watchHeadId

    const handleWarrantyAssociation = async () => {
        if (canAssociateWarranty) {
            try {
                await requestPost('/warranty/associate', {
                    component_id: watchHeadId,
                    codes: uniqueWarrantyCodes,
                    warranty_start_at: warrantyStartDate,
                })

                notifications.show({
                    title: 'Garantie associée',
                    message: 'La garantie a été associée avec succès.',
                    color: 'green',
                })

                setIsDone(true)
            } catch (e) {
                // If the error is a warranty code already associated
            }
        }
    }

    const resetStates = () => {
        setWarrantyCodes([])
        setWatchHeadId(null)
        setWarrantyStartDate(dayjs().toDate())
        setIsDone(false)
    }

    if (isDone) {
        return (
            <div className="flex flex-col p-5 gap-2 text-center">
                <span className="font-medium text-lg text-center text-green-500">
                    {warrantyCodes.length === 1
                        ? '1 carte associée'
                        : `${warrantyCodes.length} cartes associées`}
                </span>

                <IconCircleCheck size={50} className="text-green-500 mx-auto" />

                <span>
                    {'Début de la garantie'} :{' '}
                    {dayjs(warrantyStartDate).format('DD/MM/YYYY')}{' '}
                </span>

                <div className="border flex flex-col mt-5">
                    <div>
                        {warrantyCodes.map((code) => (
                            <div
                                key={code}
                                className="flex items-center justify-between p-2"
                            >
                                <div className="flex items-center gap-2">
                                    <IconQrcode size={20} />
                                    <span>{code}</span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                <Button onClick={resetStates}>
                    {'Associer de nouvelles cartes'}
                </Button>
            </div>
        )
    }

    return (
        <>
            <div className="flex flex-col p-5 gap-2">
                <span className="font-medium text-lg">
                    {'Associer des garanties'}
                </span>

                <WarrantyProductSelect
                    value={watchHeadId}
                    onChange={setWatchHeadId}
                    label={'Produit'}
                    placeholder={'Produit'}
                />

                <DatePickerInput
                    minDate={dayjs().subtract(30, 'day').toDate()}
                    maxDate={dayjs().add(30, 'day').toDate()}
                    value={warrantyStartDate}
                    onChange={setWarrantyStartDate}
                    valueFormat="LL"
                    locale="fr"
                    label={'Début de la garantie'}
                />

                <div>
                    <span className="text-sm font-medium">
                        {'Cartes à associer'}
                    </span>

                    <div className="border px-2 flex flex-col gap-1 py-2">
                        {warrantyCodes.map((code) => (
                            <div
                                key={code}
                                className="flex items-center justify-between"
                            >
                                <div className="flex items-center gap-2">
                                    <IconQrcode size={20} />
                                    <span>{code}</span>
                                </div>

                                <Button
                                    onClick={() => removeWarrantyCode(code)}
                                    size="xs"
                                    variant="subtle"
                                    color="red"
                                >
                                    {'Retirer'}
                                </Button>
                            </div>
                        ))}

                        {warrantyCodes.length === 0 && (
                            <span className="p-5 text-sm text-gray-500 text-center">
                                {'Scannez une ou plusieurs cartes de garantie'}
                            </span>
                        )}
                    </div>

                    <div className="border-x border-b p-2 flex flex-col gap-2">
                        <form
                            onSubmit={addCode}
                            className="grid grid-cols-2 gap-2"
                        >
                            <TextInput
                                value={codeToAddValue}
                                onChange={codeToAddValueHandler}
                            />

                            <Button
                                disabled={!canAddCode}
                                onClick={addCode}
                                variant="default"
                            >
                                {'Ajouter la carte'}
                            </Button>
                        </form>

                        <Button variant="default" onClick={openCamera}>
                            {'Scanner avec la caméra'}
                        </Button>
                    </div>

                    <Button
                        onClick={handleWarrantyAssociation}
                        fullWidth
                        className="mt-3"
                        disabled={!canAssociateWarranty}
                    >
                        {'Associer les cartes'}
                    </Button>
                </div>
            </div>
        </>
    )
}
